import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, ShieldCheck } from "lucide-react";

/**
 * E-signature modal for HR documents (handbook, attestation, etc.).
 * Employees type their full legal name to acknowledge. Server-side we
 * verify the typed name matches the employee record, then record
 * signed_at + signed_by + signature_ip for audit.
 */
export default function SignDocumentModal({ doc, employeeName, onClose, onSigned }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [ack, setAck] = useState(false);

  const submit = async () => {
    if (!ack) { toast.error("Confirm the acknowledgment box first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/me/hr-documents/${doc.id}/sign`, { typed_name: typed });
      toast.success("Signed — thanks for acknowledging");
      onSigned?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="sign-modal">
      <div className="bg-white border-2 border-ink max-w-lg w-full p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline mb-1 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" /> § E-SIGN · ACKNOWLEDGE</div>
            <h2 className="font-heading font-black text-2xl tracking-tight break-all">{doc.filename}</h2>
          </div>
          <button data-testid="sign-modal-close" onClick={onClose} className="hover:text-warn"><X className="w-5 h-5" /></button>
        </div>

        <div className="font-mono text-xs leading-relaxed text-muted-foreground border-l-4 border-ink pl-3">
          By typing your full legal name below, you agree that this constitutes your electronic signature under the U.S. E-SIGN Act (15 U.S.C. §7001) and any comparable state law. Your typed signature, timestamp, and IP address will be stored with your personnel file.
        </div>

        <label className="block">
          <div className="overline mb-1 text-[10px]">Type your full legal name</div>
          <input
            data-testid="sign-input"
            type="text"
            autoFocus
            value={typed}
            placeholder={employeeName}
            onChange={(e) => setTyped(e.target.value)}
            className="w-full px-3 py-3 border-2 border-ink font-mono text-lg italic tracking-wide"
          />
          <div className="font-mono text-[10px] text-muted-foreground mt-1">Must match your record: <b>{employeeName}</b></div>
        </label>

        <label className="flex items-start gap-2 cursor-pointer font-mono text-xs">
          <input
            data-testid="sign-ack-checkbox"
            type="checkbox"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-ink"
          />
          <span>I have read the document above and agree to be bound by its terms. I understand this is a binding electronic signature.</span>
        </label>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-ink/20">
          <button data-testid="sign-cancel-btn" onClick={onClose} disabled={busy} className="border-2 border-ink px-4 py-2 font-mono uppercase text-xs tracking-widest hover:bg-secondary disabled:opacity-50">Cancel</button>
          <button
            data-testid="sign-submit-btn"
            onClick={submit}
            disabled={busy || !typed.trim() || !ack}
            className="hard-btn px-5 py-2 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ShieldCheck className="w-3.5 h-3.5" /> {busy ? "Signing…" : "Sign & Acknowledge"}
          </button>
        </div>
      </div>
    </div>
  );
}
